import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import MaterialTable from 'material-table';
import tableIcons from './components/MaterialTableIcons';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import axios from 'axios';
import { api_url } from './authorize';
import { useHistory } from 'react-router-dom';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import ARID from './ARID';
import { AuthorizeContext } from './RootContext';

const theme = createTheme({
  typography: { h6: { fontSize: '1.4em' } },
});

const safeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const numberWithCommas = (value) => {
  return safeNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const normalizeSummaryRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    Acct_No: row?.Acct_No || '',
    Customer_Name: row?.Customer_Name || '',
    currency: row?.currency || '',
    Invoice_Sum: safeNumber(row?.Invoice_Sum),
    Total_Due_Sum: safeNumber(row?.Total_Due_Sum),
    Current_Due_Sum: safeNumber(row?.Current_Due_Sum),
    Past_Due_Sum: safeNumber(row?.Past_Due_Sum),
  }));
};

const Receivable = () => {
  const history = useHistory();
  const { token, setAuthBody, setAuthenticated } = useContext(AuthorizeContext);

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [detailData, setDetailData] = useState([]);
  const [detailError, setDetailError] = useState('');
  const openRequestIdRef = useRef(0);

  const goBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleAuthResponse = useCallback(
    (res) => {
      if (res?.data?.message) {
        alert(res.data.message);
        return false;
      }
      if (res?.data?.validation) {
        alert(res.data.validation.replace('_', ' '));
        return false;
      }
      if (res?.data?.notLoggedIn) {
        setAuthenticated(false);
        setAuthBody({ role: 'visitor' });
        alert('You have been logged out due to inactivity. Please log in again.');
        return false;
      }
      return true;
    },
    [setAuthBody, setAuthenticated]
  );

  const fetchSummary = useCallback(async () => {
    const res = await axios({
      url: `https://${api_url}/api/packing_invoice?lookup=AR`,
      method: 'get',
      headers: {
        'x-access-token': token,
      },
    });

    if (!handleAuthResponse(res)) return;
    setTableData(normalizeSummaryRows(res.data));
  }, [handleAuthResponse, token]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        await fetchSummary();
      } catch (error) {
        if (mounted) {
          alert(error?.response?.data?.message || 'Failed to load receivables.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [fetchSummary]);

  const fetchDetail = useCallback(
    async (acctNo) => {
      const requestId = ++openRequestIdRef.current;
      setDetailLoading(true);
      setDetailError('');
      setDetailData([]);

      try {
        const res = await axios({
          url: `https://${api_url}/api/packing_invoice?lookup=AR_DETAIL&acctid=${encodeURIComponent(acctNo)}`,
          method: 'get',
          headers: {
            'x-access-token': token,
          },
        });

        if (requestId !== openRequestIdRef.current) return;

        if (!handleAuthResponse(res)) {
          setDetailLoading(false);
          return;
        }

        const invoices = Array.isArray(res?.data?.Unpaid_invoices)
          ? res.data.Unpaid_invoices
          : Array.isArray(res?.data?.invoices)
          ? res.data.invoices
          : [];

        setDetailData(invoices);
      } catch (error) {
        if (requestId !== openRequestIdRef.current) return;
        setDetailError(error?.response?.data?.message || 'Failed to load invoice detail.');
      } finally {
        if (requestId === openRequestIdRef.current) {
          setDetailLoading(false);
        }
      }
    },
    [handleAuthResponse, token]
  );

  const handleClickOpen = useCallback(
    (rowData) => async () => {
      const safeRow = rowData || null;
      setChosen(safeRow);
      setOpen(true);
      setDetailData([]);
      setDetailError('');

      if (!safeRow?.Acct_No) {
        setDetailError('Missing account number.');
        return;
      }

      await fetchDetail(safeRow.Acct_No);
    },
    [fetchDetail]
  );

  const handleClose = useCallback((e) => {
    if (e?.preventDefault) e.preventDefault();
    openRequestIdRef.current += 1;
    setOpen(false);
    setDetailLoading(false);
    setDetailError('');
    setDetailData([]);
    setChosen(null);
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'Acct#',
        field: 'Acct_No',
        cellStyle: { fontSize: '1em' },
        editable: 'never',
      },
      {
        title: 'Distributor Name',
        field: 'Customer_Name',
        align: 'center',
        editable: 'never',
        render: (rowData) => (
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickOpen(rowData)}
            >
              {rowData.Customer_Name}
            </Button>
          </div>
        ),
        cellStyle: { fontSize: '.8em' },
      },
      {
        title: 'Currency',
        field: 'currency',
        align: 'right',
        editable: 'never',
        render: (rowData) => <div>{rowData.currency}</div>,
        cellStyle: { fontSize: '.8em' },
      },
      {
        title: 'Invoice Amount',
        field: 'Invoice_Sum',
        align: 'right',
        editable: 'never',
        render: (rowData) => <div>{numberWithCommas(rowData.Invoice_Sum)}</div>,
        cellStyle: { fontSize: '.8em' },
      },
      {
        title: 'Due Amount',
        field: 'Total_Due_Sum',
        align: 'right',
        editable: 'never',
        render: (rowData) => (
          <div style={{ textAlign: 'right' }}>
            {numberWithCommas(rowData.Total_Due_Sum)}
          </div>
        ),
        cellStyle: { fontSize: '.8em' },
      },
      {
        title: 'Current Amount',
        field: 'Current_Due_Sum',
        align: 'right',
        editable: 'never',
        render: (rowData) => (
          <div style={{ textAlign: 'right' }}>
            {numberWithCommas(rowData.Current_Due_Sum)}
          </div>
        ),
        cellStyle: { fontSize: '.8em', marginRight: 45 },
      },
      {
        title: 'Past Due Amount',
        field: 'Past_Due_Sum',
        align: 'right',
        editable: 'never',
        render: (rowData) => (
          <div style={{ textAlign: 'right' }}>
            {numberWithCommas(rowData.Past_Due_Sum)}
          </div>
        ),
        cellStyle: { fontSize: '.8em', marginRight: 45 },
      },
    ],
    [handleClickOpen]
  );

  const options = useMemo(
    () => ({
      sorting: true,
      search: true,
      searchFieldAlignment: 'right',
      searchAutoFocus: false,
      searchFieldVariant: 'outlined',
      filtering: false,
      paging: true,
      pageSizeOptions: [5, 10, 15, 50],
      pageSize: 10,
      paginationType: 'stepped',
      showFirstLastPageButtons: false,
      paginationPosition: 'bottom',
      exportButton: { csv: true },
      exportAllData: true,
      exportFileName: 'Receivable',
      addRowPosition: 'first',
      actionsColumnIndex: -1,
      selection: false,
      showSelectAllCheckbox: false,
      showTextRowsSelected: false,
      grouping: false,
      columnsButton: true,
      debounceInterval: 500,
      rowStyle: (data, index) => (index % 2 === 0 ? { background: '#f5f5f5' } : null),
      headerStyle: { background: 'lightgray', fontStyle: 'italic', fontSize: 19 },
    }),
    []
  );

  return (
    <div className="Receivable">
      <h1 align="center">Receivable</h1>

      <button onClick={goBack}>Back</button>

      <ThemeProvider theme={theme}>
        <MaterialTable
          title="*Accounts Receivable"
          icons={tableIcons}
          columns={columns}
          data={tableData}
          isLoading={loading}
          options={options}
        />
      </ThemeProvider>

      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title" />
        <DialogContent>
          {detailError ? (
            <div style={{ padding: 16, color: 'red', fontSize: '1.1em' }}>{detailError}</div>
          ) : (
            <ARID
              data={detailData}
              name={chosen?.Customer_Name || ''}
              id={"Receivable doesn't need Customer ID"}
              sum={chosen?.Total_Due_Sum || 0}
              currency={chosen?.currency || ''}
              loading={detailLoading}
            />
          )}

          <DialogActions>
            <button
              className="btn btn-primary"
              onClick={handleClose}
              style={{ fontSize: '2em' }}
            >
              Close
            </button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Receivable;